import { useEffect, useRef, type ReactNode } from 'react';
import { LockSimple, WarningCircle } from '@phosphor-icons/react';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import { importIdentityWording } from './importIdentityWording';
import { USERNAME_KEY, extractHandleClient, isBlank } from './importWizardHelpers';
import { ReviewCellMessage } from './ReviewCellMessage';
import { cellVersions, currentValueNote, isLockedCell, lockedCellTitle, type CellVersions } from './reviewGridDraft';
import { reviewInputAttributes, reviewInputKind } from './reviewInputKind';

const ADD_VALUE_TEXT = 'Add value';
const EDIT_HINT = 'Click to edit';
const CELL_CLASS = 'flex min-w-0 items-center px-2';
// The view button IS the cell, edge to edge, so an error or pending tint
// fills the whole cell instead of sitting in it as a chip.
const VIEW_CELL_CLASS = 'flex min-w-0';
const VIEW_BUTTON_CLASS =
  'flex size-full min-w-0 items-center gap-1.5 px-4 text-left text-body-sm-regular text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
const EDIT_INPUT_CLASS = 'h-8 bg-background px-2 text-body-sm-regular shadow-none aria-invalid:bg-destructive/10';
const ICON_SIZE = 14;

/** `pending` = the row still waits for its Current/Sheet pick; `warning` =
 *  a value the import will leave out without blocking the row. */
type CellTone = 'plain' | 'pending' | 'warning' | 'error';

const TONE_CLASS: Record<CellTone, string> = {
  plain: 'hover:bg-muted',
  pending: 'bg-background-warning-light-hover',
  warning: 'bg-background-warning-light-hover',
  error: 'bg-destructive/10',
};

function cellTone(error: string | undefined, warning: string | undefined, contentTone: CellTone): CellTone {
  if (error !== undefined) return 'error';
  if (warning !== undefined) return 'warning';
  return contentTone;
}

interface CellContent {
  body: ReactNode;
  /** Native hover hint, used only when the cell has no message. */
  title: string;
  tone: CellTone;
  /** Worth saying out loud: shown in the tooltip and read to screen readers. */
  message?: string;
}

function isEmptyOptionalOnNewCreator(row: ImportBatchRow, field: ExpectedField): boolean {
  if (field.required || row.rosterMatch !== null) return false;
  return isBlank(row.values[field.key]);
}

function VersionedValue({ versions }: { versions: CellVersions }) {
  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="truncate">{versions.kept}</span>
      <span className="truncate text-muted-foreground line-through">{versions.other}</span>
    </span>
  );
}

function LockedValue({ value }: { value: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
      <LockSimple size={ICON_SIZE} aria-hidden="true" />
      <span className="truncate">{value}</span>
    </span>
  );
}

function plainText(text: string, title: string): CellContent {
  return { body: <span className="truncate">{text}</span>, title, tone: 'plain' };
}

// An invalid cell shows what the admin typed: the handle extracted from a bad
// link is misleading (a reel link would read as the creator "reel").
function identityText(raw: string, invalid: boolean, campaignPlatform: string): string {
  return invalid ? raw : extractHandleClient(raw, campaignPlatform);
}

function identityContent(row: ImportBatchRow, invalid: boolean, campaignPlatform: string): CellContent {
  const raw = row.values[USERNAME_KEY] ?? '';
  if (isBlank(raw)) return plainText(importIdentityWording(campaignPlatform).emptyCellPrompt, EDIT_HINT);
  return plainText(identityText(raw, invalid, campaignPlatform), raw);
}

function lockedContent(row: ImportBatchRow, field: ExpectedField): CellContent {
  const value = row.rosterMatch?.currentValues[field.key] ?? '';
  return { body: <LockedValue value={value} />, title: lockedCellTitle(row, field), tone: 'plain' };
}

// Muted while it is only an invitation; on an error tint it keeps the normal
// text colour, like every other flagged cell.
function addValueContent(invalid: boolean): CellContent {
  if (invalid) return plainText(ADD_VALUE_TEXT, EDIT_HINT);
  return { body: <span className="truncate text-muted-foreground">{ADD_VALUE_TEXT}</span>, title: EDIT_HINT, tone: 'plain' };
}

function versionedContent(versions: CellVersions): CellContent {
  if (versions.decided) return { body: <VersionedValue versions={versions} />, title: EDIT_HINT, tone: 'plain' };
  return { ...plainText(versions.kept, EDIT_HINT), tone: 'pending', message: currentValueNote(versions.other) };
}

// OWNER RULE: values (Avg views / Engagement included) are shown exactly as
// the server stored them — no reformatting here.
function valueContent(
  row: ImportBatchRow,
  field: ExpectedField,
  invalid: boolean,
  campaignPlatform: string,
  payoutModel: string | undefined,
): CellContent {
  if (field.key === USERNAME_KEY) return identityContent(row, invalid, campaignPlatform);
  if (isLockedCell(row, field)) return lockedContent(row, field);
  const versions = cellVersions(row, field, payoutModel);
  if (versions) return versionedContent(versions);
  if (isEmptyOptionalOnNewCreator(row, field)) return addValueContent(invalid);
  return plainText(row.values[field.key] ?? '', EDIT_HINT);
}

interface ReviewCellProps {
  row: ImportBatchRow;
  field: ExpectedField;
  campaignPlatform: string;
  /** Decides whether a ₹0 price is one the import sets (see cellVersions). */
  payoutModel?: string;
  onStartEdit: () => void;
}

export function ReviewCell({ row, field, campaignPlatform, payoutModel, onStartEdit }: ReviewCellProps) {
  const error = row.validationErrors[field.key];
  const warning = row.validationWarnings[field.key];
  const invalid = error !== undefined;
  const content = valueContent(row, field, invalid, campaignPlatform, payoutModel);
  const message = error ?? warning ?? content.message;
  const tone = cellTone(error, warning, content.tone);
  // With a message the tooltip speaks; a native title on top would show twice.
  const title = message === undefined ? content.title : undefined;
  return (
    <div role="cell" className={VIEW_CELL_CLASS}>
      <ReviewCellMessage message={message}>
        <button
          type="button"
          title={title}
          aria-invalid={invalid || undefined}
          onClick={onStartEdit}
          className={cn(VIEW_BUTTON_CLASS, TONE_CLASS[tone])}
        >
          {invalid && <WarningCircle size={ICON_SIZE} aria-hidden="true" className="shrink-0 text-destructive" />}
          {content.body}
        </button>
      </ReviewCellMessage>
    </div>
  );
}

/** Edit mode's cell for a locked match's price or usage-rights days: commit
 *  ignores them, so there is nothing to type — it shows the current value. */
export function ReviewLockedCell({ row, field }: { row: ImportBatchRow; field: ExpectedField }) {
  const content = lockedContent(row, field);
  return (
    <div role="cell" title={content.title} className={cn(CELL_CLASS, 'text-body-sm-regular')}>
      {content.body}
    </div>
  );
}

interface ReviewEditCellProps {
  rowNumber: number;
  field: ExpectedField;
  /** The column's name as the grid header shows it. */
  label: string;
  value: string;
  error: string | undefined;
  deleted: boolean;
  /** Set on the one cell the admin clicked into edit mode from: its input
   *  takes focus when it mounts, so typing can start right away. */
  focusOnMount?: boolean;
  onFocused?: () => void;
  onChange: (value: string) => void;
}

export function ReviewEditCell({ rowNumber, field, label, value, error, deleted, focusOnMount = false, onFocused, onChange }: ReviewEditCellProps) {
  const invalid = error !== undefined;
  const attributes = reviewInputAttributes(reviewInputKind(field));
  // Only a cell the admin typed in is trimmed: tabbing through an untouched
  // padded cell must not turn it into an edit.
  const typedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mount-only on purpose: the view button this input replaces held focus.
  useEffect(() => {
    if (!focusOnMount) return;
    inputRef.current?.focus();
    onFocused?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must not refocus when the draft or callbacks change.
  }, []);

  function changeValue(next: string) {
    typedRef.current = true;
    onChange(next);
  }

  function trimOnBlur() {
    if (!typedRef.current) return;
    const trimmed = value.trim();
    if (trimmed !== value) onChange(trimmed);
  }

  return (
    <div role="cell" className={CELL_CLASS}>
      <ReviewCellMessage message={error}>
        <Input
          ref={inputRef}
          type="text"
          {...attributes}
          aria-label={`${label}, row ${rowNumber}`}
          aria-invalid={invalid || undefined}
          value={value}
          disabled={deleted}
          onChange={(event) => changeValue(event.target.value)}
          onBlur={trimOnBlur}
          className={EDIT_INPUT_CLASS}
        />
      </ReviewCellMessage>
    </div>
  );
}
